<template>
    <div class="calendar-container">
        <FullCalendar
            ref="calendar"
            :all-day-slot="false"
            :button-text="calendarOptions.buttonText"
            :dates-render="renderCalendarDates"
            :droppable="true"
            :editable="true"
            :event-limit=5
            :event-render="calendarEventRender"
            :event-sources="calendarEventSources"
            :event-time-format="calendarOptions.eventTimeFormat"
            :eventStartEditable="true"
            :fixed-week-count="false"
            :header="calendarOptions.header"
            :locale="calendarOptions.locale"
            :locales="calendarOptions.locales"
            :navLinks="true"
            :nowIndicator="true"
            :plugins="calendarOptions.plugins"
            :scroll-time="calendarOptions.scrollTime"
            :selectable="calendarOptions.selectable"
            :time-zone="calendarOptions.timeZone"
            event-order="sortIdx"
            style="flex: 4 1 0"
            @drop="programsDrop"
            @eventClick="selectCalendarDate"
            @eventDragStart="handleEventDragStart"
            @eventDragStop="handleEventDragStop"
            @eventDrop="handleEventDrop"
            @eventResize="handleEventResize"
        >
            <template v-slot:eventContent='arg'>
                <div><b v-if="checkHideEvent(arg.event)">hide</b><i>{{ arg.event.title }}</i></div>
            </template>
        </FullCalendar>
    </div>
</template>

<script>
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import fullCalendarBootstrap from '@fullcalendar/bootstrap';
import koLocale from '@fullcalendar/core/locales/ko';
import jaLocale from '@fullcalendar/core/locales/ja';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import FullCalendar from '@fullcalendar/vue';
import LiveScheduleError from '@/exception/LiveScheduleError';
import _ from 'lodash';

export default {
    name: 'SchedulesCalendar',

    components: {
        FullCalendar,
    },

    computed: {
        ...mapGetters('schedule', ['schedules', 'selectedRecordingChannels', 'gridDateStart']),
        ...mapGetters('schedule_register_modal', ['program']),
        ...mapGetters('calendar', ['refetchEvents', 'calendarDefaultView']),
        ...mapGetters('media_contents', ['mediaContents']),
        calendarEventSources() {
            const self = this;
            return [
                {
                    events(info, callback) {
                        self.setGridDate({
                            start: self.$moment(info.startStr).utc().format('YYYY-MM-DD 00:00:00'),
                            end: self.$moment(info.endStr).utc().format('YYYY-MM-DD 23:59:59'),
                        });
                        callback(self.schedules);
                    },
                },
            ];
        },
        calendarApi() {
            return this.$refs.calendar.getApi();
        },
    },
    watch: {
        schedules(value) {
            this.calendarApi.refetchEvents();

            if (this.program.id > 0) {
                const updatedProgram = value.find(el => el.live_program_id === this.program.id);

                if (!_.isEqual(updatedProgram, this.program)) {
                    this.fetchSchedule(updatedProgram.id);
                }
            }
        },
        refetchEvents(value) {
            if (value) {
                this.calendarApi.refetchEvents();
                this.setRefetchEvents(false);
            }
        },
        gridDateStart(value) {
            if (value) {
                this.fetchSchedules();
            }
        },
        'calendarOptions.defaultView': {
            handler(newValue) {
                this.setCalendarScrollTime();
                if (typeof newValue !== 'undefined') {
                    localStorage.setItem('calendar_view', newValue);
                }
            },
            deep: true,
        },
    },
    data() {
        return {
            calendarOptions: {
                header: {
                    left: 'prev,today,next , dayGridMonth,timeGridWeek,timeGridDay',
                    center: '',
                    right: 'title',
                },
                buttonText: {
                    month: window.Laravel.lang === 'en' ? 'Month' : null,
                    week: window.Laravel.lang === 'en' ? 'Week' : null,
                },
                plugins: [
                    dayGridPlugin,
                    timeGridPlugin,
                    interactionPlugin, // needed for dateClick
                    fullCalendarBootstrap,
                ],
                locales: [
                    koLocale,
                    jaLocale,
                ],
                locale: window.Laravel.lang,
                timeZone: window.Laravel.tz,
                selectable: true,
                defaultView: 'dayGridMonth',
                eventTimeFormat: {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true,
                },
                scrollTime: '06:00:00',
            },
        };
    },
    mounted() {
        this.initCalendarDefaultView();
    },
    methods: {
        ...mapMutations({
            setGridDate: 'schedule/setGridDate',
            setRefetchEvents: 'calendar/setRefetchEvents',
            setCalendarDefaultView: 'calendar/setCalendarDefaultView',
            setRegisterModalShow: 'schedule_register_modal/setRegisterModalShow',
            setPickDateTime: 'schedule_register_modal/setPickDateTime',
            setProgramFile: 'schedule_register_modal/setProgramFile',
            setEditProgram: 'schedule_register_modal/setEditProgram',
            addProgramFile: 'schedule_register_modal/addProgramFile',
            clearProgram: 'schedule_register_modal/clearProgram',
        }),
        ...mapActions({
            fetchSchedule: 'schedule_register_modal/fetchSchedule',
            fetchSchedules: 'schedule/fetchSchedules',
            updateSchedule: 'schedule_register_modal/updateSchedule',
        }),
        initCalendarDefaultView() {
            if (localStorage.getItem('calendar_view')) {
                const calendarView = localStorage.getItem('calendar_view');
                if (calendarView === 'timeGridWeek' || calendarView === 'dayGridMonth') {
                    this.calendarOptions.defaultView = calendarView;
                }
            }

            this.setCalendarScrollTime();
        },
        renderCalendarDates(info) {
            this.calendarOptions.defaultView = info.view.viewSpec.type;
            this.setCalendarDefaultView(info.view.viewSpec.type);
        },
        calendarEventRender(info) {
            if (info.isMirror || _.isEmpty(info.event.extendedProps)) {
                return false;
            }

            $(info.el).addClass(`schedule__${info.event.extendedProps.bgClassName}`);
            $(info.el).prepend(`<span class="schedule__left-highlight ${info.event.extendedProps.bgClassName}"></span>`);
            if (this.checkHideEvent(info.event.extendedProps)) {
                $(info.el).find('.fc-title').prepend(`
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style="scale: 0.9; margin-top: -1px">
                    <g clip-path="url(#clip0_123_15205)">
                        <path d="M11.9216 12.8645C10.7874 13.5837 9.44237 14.0001 8.00004 14.0001C4.40525 14.0001 1.4146 11.4136 0.787598 8.00015C1.07872 6.41523 1.87938 5.00861 3.01348 3.95639L0.928944 1.87186L1.87176 0.929047L15.0711 14.1283L14.1283 15.0712L11.9216 12.8645ZM3.9569 4.89981C3.07099 5.70678 2.42504 6.77815 2.14836 8.00015C2.75998 10.7014 5.17624 12.6668 8.00004 12.6668C9.06631 12.6668 10.0745 12.3865 10.9494 11.8923L9.59717 10.5401C9.1347 10.8315 8.58704 11.0001 8.00004 11.0001C6.34316 11.0001 5.00001 9.65695 5.00001 8.00015C5.00001 7.41308 5.16862 6.86541 5.46004 6.40296L3.9569 4.89981ZM8.60911 9.55201L6.44814 7.39108C6.37404 7.57975 6.33334 7.78515 6.33334 8.00015C6.33334 8.92061 7.07957 9.66681 8.00004 9.66681C8.21497 9.66681 8.42044 9.62608 8.60911 9.55201ZM13.871 11.0617L12.9172 10.1078C13.3544 9.48455 13.6768 8.77241 13.8516 8.00015C13.24 5.2988 10.8238 3.33345 8.00004 3.33345C7.4361 3.33345 6.88844 3.41183 6.36817 3.5588L5.31614 2.50678C6.14735 2.17974 7.0527 2.00012 8.00004 2.00012C11.5948 2.00012 14.5854 4.58663 15.2124 8.00015C15.0043 9.13321 14.5357 10.1752 13.871 11.0617ZM7.8151 5.00573C7.87624 5.00201 7.9379 5.00012 8.00004 5.00012C9.65684 5.00012 11 6.34327 11 8.00015C11 8.06221 10.9981 8.12388 10.9944 8.18501L7.8151 5.00573Z" fill="#212529"/>
                    </g>
                    <defs>
                        <clipPath id="clip0_123_15205">
                            <rect width="16" height="16" fill="white"/>
                        </clipPath>
                    </defs>
                </svg>
                `);
            }

            return true;
        },
        checkHideEvent(program) {
            return ('hided_at' in program.live_media_channel && !_.isEmpty(program.live_media_channel.hided_at));
        },
        renderCalendarEvents() {
            const calendarApi = this.$refs.calendar.getApi();
            calendarApi.refetchEvents();
        },
        async selectCalendarDate(info) {
            // 스케쥴 클릭 시 실행 할것들
            // 해당 스케쥴 register 모달 노출
            // info.event.extendedProps 에서 title을 안 건네줘서 find로 해당 요소 찾음
            const liveProgramId = Number(info.event.id);
            const liveProgram = this.schedules.find(el => el.id === liveProgramId);
            if (liveProgram) {
                await this.fetchSchedule(liveProgramId);
                await this.setRegisterModalShow(true);
            }
        },
        programsDrop(e) {
            const { contentId, selectedContentsIds } = e.draggedEl.dataset;
            const pickTime = this.$moment(e.date).subtract(9, 'hours').toDate();
            this.setPickDateTime(pickTime);

            if (selectedContentsIds) {
                // html data 로  데이터를 주고 받을 때 string 형식 밖에 지원안하여 JSON.stringfy 형태로 주고 받음
                const parseSelectedContentsIds = JSON.parse(selectedContentsIds);
                const files = this.mediaContents.data.filter(el => (parseSelectedContentsIds.find(contentsId => contentsId === el.id)));
                const haveNoneTranscodingDoneFiles = files.filter(el => el.transcoding_state !== 'transcoding done');

                if (haveNoneTranscodingDoneFiles.length > 0) {
                    this.$toasted.error(this.$t('paragraphs.program_have_none_transcoding_done_files'));
                    return;
                }
                this.setProgramFile(files);

                delete e.draggedEl.dataset.selectedContentsIds;
            } else {
                const file = this.mediaContents.data.find(el => el.id === Number(contentId));

                if (file.transcoding_state !== 'transcoding done') {
                    this.$toasted.error(this.$t('paragraphs.program_have_none_transcoding_done_files'));
                    return;
                }

                this.addProgramFile(file);
            }

            this.setRegisterModalShow(true);
        },
        handleEventDragStart(e) {
            // console.log('Drag Start', e);
        },
        handleEventDragStop(e) {
            // console.log('Drag End', e);
        },
        async handleEventDrop(info) {
            try {
                const editableBeforeProgramStatus = this.programLiveStatus(info.oldEvent.start, info.oldEvent.end);
                const editableAfterProgramStatus = this.programLiveStatus(info.event.start, info.event.end);

                if (editableBeforeProgramStatus === 'live') {
                    throw new LiveScheduleError(this.$t('paragraphs.schedule_live_on_modify_error'));
                }

                if (editableBeforeProgramStatus === 'end') {
                    throw new LiveScheduleError(this.$t('paragraphs.schedule_live_end_modify_error'));
                }

                if (editableAfterProgramStatus !== 'before') {
                    throw new LiveScheduleError(this.$t('paragraphs.program_can_register_after_5min'));
                }

                const liveProgramId = Number(info.event.id);
                await this.fetchSchedule(liveProgramId);
                const startTime = this.$moment(info.event.start).subtract(9, 'hours').toDate();
                await this.setPickDateTime(startTime);
                await this.updateSchedule();

                this.$toasted.success(this.$t('paragraphs.schedule_live_modify_success'));
                await this.fetchSchedules();
            } catch (e) {
                if (e instanceof LiveScheduleError) {
                    this.$toasted.error(e.message);
                } else if (e.response.status === 400) {
                    this.$toasted.error(e.response.data.message);
                } else {
                    this.$toasted.error(this.$t('paragraphs.schedule_live_modify_fail'));
                }
                info.revert();
            } finally {
                //
            }
        },
        programLiveStatus(start, end) {
            let status = '';
            const currentTime = this.$moment().unix();
            const startUnixTime = this.$moment(start).subtract(9, 'hours').unix();
            const endUnixTime = this.$moment(end).subtract(9, 'hours').unix();


            if (currentTime < startUnixTime) {
                status = 'before';
            } else if (currentTime >= startUnixTime && currentTime <= endUnixTime) {
                status = 'live';
            } else if (currentTime > endUnixTime) {
                status = 'end';
            } else {
                status = 'none';
            }

            return status;
        },
        setCalendarScrollTime() {
            const now = this.$moment();
            const before2Hours = this.$moment().subtract(2, 'hours');

            if (now.format('YYYY-MM-DD') !== before2Hours.format('YYYY-MM-DD')) {
                this.calendarOptions.scrollTime = '14:00:00';
            } else {
                this.calendarOptions.scrollTime = this.$moment().subtract(2, 'hours').format('HH:00:00');
            }
        },
        handleEventResize(info) {
            info.revert();
        },
    },
};
</script>
